import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { fetchMyLeaves } from '../../services/attendanceService';

const LEAVE_TYPES = [
  { value: 'HALF_DAY', label: 'Half day' },
  { value: 'FULL_DAY', label: 'Full day' },
];

const STATUS_STYLES = {
  PENDING: 'bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200',
  APPROVED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200',
  ACTIVE: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200',
  REJECTED: 'bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-200',
  CANCELLED: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
};

const addHoursToTime = (timeValue, hoursToAdd = 5) => {
  if (!timeValue || !/^\d{2}:\d{2}$/.test(timeValue)) return '';
  const [h, m] = timeValue.split(':').map(Number);
  const total = (h * 60 + m + hoursToAdd * 60) % (24 * 60);
  const nh = Math.floor(total / 60);
  const nm = total % 60;
  return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
};

const currentTimeValue = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
};

const formatDateInput = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const leavePeriodLabel = (leave) => {
  if (leave.eventType === 'LEAVE_REQUEST') {
    if (leave.fromDateLabel && leave.toDateLabel) {
      return leave.fromDateLabel === leave.toDateLabel
        ? leave.fromDateLabel
        : `${leave.fromDateLabel} → ${leave.toDateLabel}`;
    }
    if (leave.fromDate && leave.toDate) {
      return leave.fromDate === leave.toDate
        ? leave.fromDate
        : `${leave.fromDate} → ${leave.toDate}`;
    }
    return 'Date range';
  }
  if (leave.leaveType === 'HALF_DAY') {
    const timing = [leave.startTimeLabel || leave.startTime, leave.endTimeLabel || leave.endTime]
      .filter(Boolean)
      .join(' – ');
    return `${leave.leaveTypeLabel || 'Half day'}${timing ? ` · ${timing}` : ''}`;
  }
  return leave.leaveTypeLabel || 'Full day';
};

const statusLabel = (status) => {
  const value = (status || 'PENDING').toUpperCase();
  if (value === 'ACTIVE') return 'Approved';
  return value.charAt(0) + value.slice(1).toLowerCase();
};

const ApplyLeaveModal = ({ onClose, onApplyToday, onRequestFuture, onCancelLeave }) => {
  const [mode, setMode] = useState('today'); // today | future
  const [leaveType, setLeaveType] = useState('FULL_DAY');
  const initialStart = useMemo(() => currentTimeValue(), []);
  const [startTime, setStartTime] = useState(initialStart);
  const [endTime, setEndTime] = useState(() => addHoursToTime(initialStart, 5));
  const today = useMemo(() => formatDateInput(new Date()), []);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cancellingId, setCancellingId] = useState('');
  const [error, setError] = useState('');
  const [leaves, setLeaves] = useState([]);
  const [loadingLeaves, setLoadingLeaves] = useState(true);

  const todayLabel = useMemo(() => new Date().toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }), []);

  const loadLeaves = useCallback(async () => {
    setLoadingLeaves(true);
    try {
      const data = await fetchMyLeaves();
      setLeaves(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load leave history');
    } finally {
      setLoadingLeaves(false);
    }
  }, []);

  useEffect(() => {
    loadLeaves();
  }, [loadLeaves]);

  useEffect(() => {
    if (leaveType === 'HALF_DAY' && startTime) {
      setEndTime(addHoursToTime(startTime, 5));
    }
  }, [leaveType, startTime]);

  const handleLeaveTypeChange = (value) => {
    setLeaveType(value);
    setError('');
    if (value === 'HALF_DAY') {
      const now = currentTimeValue();
      setStartTime(now);
      setEndTime(addHoursToTime(now, 5));
    }
  };

  const handleSubmit = async () => {
    if (submitting) return;
    const trimmed = reason.trim();
    if (!trimmed) {
      setError('Please enter a reason for leave');
      return;
    }

    if (mode === 'today') {
      if (leaveType === 'HALF_DAY') {
        if (!startTime || !endTime) {
          setError('Please select start time and end time for half day leave');
          return;
        }
        if (endTime <= startTime) {
          setError('End time must be after start time');
          return;
        }
      }
      setSubmitting(true);
      setError('');
      try {
        await onApplyToday({
          leaveType,
          reason: trimmed,
          startTime: leaveType === 'HALF_DAY' ? startTime : null,
          endTime: leaveType === 'HALF_DAY' ? endTime : null,
        });
        setReason('');
        await loadLeaves();
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || 'Failed to apply leave');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (!fromDate || !toDate) {
      setError('Please select from date and to date');
      return;
    }
    if (toDate < fromDate) {
      setError('To date cannot be before from date');
      return;
    }
    if (fromDate < today) {
      setError('Leave request cannot start in the past');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await onRequestFuture(fromDate, toDate, trimmed);
      setReason('');
      await loadLeaves();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to request leave');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelLeave = async (leaveId) => {
    if (!leaveId || cancellingId) return;
    setCancellingId(leaveId);
    setError('');
    try {
      const result = await onCancelLeave?.(leaveId);
      if (Array.isArray(result?.leaves)) {
        setLeaves(result.leaves);
      } else {
        await loadLeaves();
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to cancel leave');
    } finally {
      setCancellingId('');
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-3 sm:p-5 bg-black/55 backdrop-blur-[2px]">
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl shadow-2xl border border-amber-200/70 dark:border-amber-800 bg-white dark:bg-gray-800 flex flex-col"
        role="dialog"
        aria-labelledby="apply-leave-title"
      >
        <div className="relative px-5 sm:px-6 pt-5 pb-4 bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 text-white flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-white/20 transition-colors z-10"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <p className="text-amber-50/90 text-xs sm:text-sm font-medium">
            Today · {todayLabel}
          </p>
          <h2 id="apply-leave-title" className="text-2xl font-bold tracking-tight mt-1 pr-8">
            Apply Leave
          </h2>
        </div>

        <div className="px-5 sm:px-6 py-5 space-y-5 overflow-y-auto min-h-0 flex-1">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/40 dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => { setMode('today'); setError(''); }}
              className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${
                mode === 'today'
                  ? 'border-amber-400 bg-amber-50 text-amber-900 dark:border-amber-600 dark:bg-amber-950/50 dark:text-amber-100'
                  : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
            >
              Leave for today
            </button>
            <button
              type="button"
              onClick={() => { setMode('future'); setError(''); }}
              className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${
                mode === 'future'
                  ? 'border-amber-400 bg-amber-50 text-amber-900 dark:border-amber-600 dark:bg-amber-950/50 dark:text-amber-100'
                  : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
            >
              Future leave
            </button>
          </div>

          {mode === 'today' ? (
            <>
              <div className="space-y-1.5">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                  Leave duration
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {LEAVE_TYPES.map((option) => {
                    const selected = leaveType === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleLeaveTypeChange(option.value)}
                        className={`rounded-xl border px-3 py-3 text-sm font-semibold transition-colors ${
                          selected
                            ? 'border-amber-400 bg-amber-50 text-amber-900 dark:border-amber-600 dark:bg-amber-950/50 dark:text-amber-100'
                            : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {leaveType === 'HALF_DAY' && (
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                    Half day timing
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label
                        htmlFor="half-day-start"
                        className="text-[10px] font-bold text-gray-400 uppercase tracking-wider"
                      >
                        Start time
                      </label>
                      <input
                        id="half-day-start"
                        type="time"
                        value={startTime}
                        onChange={(e) => {
                          setStartTime(e.target.value);
                          setError('');
                        }}
                        className="w-full text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label
                        htmlFor="half-day-end"
                        className="text-[10px] font-bold text-gray-400 uppercase tracking-wider"
                      >
                        End time
                      </label>
                      <input
                        id="half-day-end"
                        type="time"
                        value={endTime}
                        onChange={(e) => {
                          setEndTime(e.target.value);
                          setError('');
                        }}
                        className="w-full text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none"
                      />
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    End time defaults to start time + 5 hours.
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label
                  htmlFor="leave-from-date"
                  className="text-[10px] font-bold text-gray-400 uppercase tracking-wider"
                >
                  From date
                </label>
                <input
                  id="leave-from-date"
                  type="date"
                  value={fromDate}
                  min={today}
                  onChange={(e) => {
                    const next = e.target.value;
                    setFromDate(next);
                    if (toDate && toDate < next) setToDate(next);
                    setError('');
                  }}
                  className="w-full text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none"
                />
              </div>
              <div className="space-y-1.5">
                <label
                  htmlFor="leave-to-date"
                  className="text-[10px] font-bold text-gray-400 uppercase tracking-wider"
                >
                  To date
                </label>
                <input
                  id="leave-to-date"
                  type="date"
                  value={toDate}
                  min={fromDate || today}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    setError('');
                  }}
                  className="w-full text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none"
                />
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <label
              htmlFor="apply-leave-reason"
              className="text-[10px] font-bold text-gray-400 uppercase tracking-wider"
            >
              Reason for leave
            </label>
            <textarea
              id="apply-leave-reason"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setError('');
              }}
              rows={3}
              maxLength={500}
              placeholder={mode === 'today' ? 'Why are you applying leave today?' : 'Why do you need leave for these dates?'}
              className="w-full text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none resize-none"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                Your leave & status
              </p>
              <button
                type="button"
                onClick={loadLeaves}
                className="text-[11px] font-semibold text-amber-700 dark:text-amber-300 hover:underline"
              >
                Refresh
              </button>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-900/80 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    <tr>
                      <th className="px-3 py-2 font-bold">Type</th>
                      <th className="px-3 py-2 font-bold">Period</th>
                      <th className="px-3 py-2 font-bold">Status</th>
                      <th className="px-3 py-2 font-bold text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700/80">
                    {loadingLeaves ? (
                      <tr>
                        <td colSpan={4} className="px-3 py-6 text-center text-gray-500 dark:text-gray-400">
                          Loading leaves…
                        </td>
                      </tr>
                    ) : leaves.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-3 py-6 text-center text-gray-500 dark:text-gray-400">
                          No leave records yet
                        </td>
                      </tr>
                    ) : (
                      leaves.map((leave) => {
                        const status = (leave.status || 'PENDING').toUpperCase();
                        return (
                          <tr key={leave.id} className="bg-white dark:bg-gray-800/40">
                            <td className="px-3 py-2.5 align-top">
                              <p className="font-semibold text-gray-900 dark:text-white">
                                {leave.eventType === 'LEAVE_REQUEST' ? 'Future' : 'Today'}
                              </p>
                              {leave.reason && (
                                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2 max-w-[10rem]">
                                  {leave.reason}
                                </p>
                              )}
                            </td>
                            <td className="px-3 py-2.5 align-top text-gray-700 dark:text-gray-200">
                              {leavePeriodLabel(leave)}
                            </td>
                            <td className="px-3 py-2.5 align-top">
                              <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${STATUS_STYLES[status] || STATUS_STYLES.PENDING}`}>
                                {statusLabel(status)}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 align-top text-right">
                              {leave.canCancel ? (
                                <button
                                  type="button"
                                  disabled={Boolean(cancellingId)}
                                  onClick={() => handleCancelLeave(leave.id)}
                                  className="text-[11px] font-bold text-rose-600 dark:text-rose-400 hover:underline disabled:opacity-60"
                                >
                                  {cancellingId === leave.id ? 'Reverting…' : 'Revert'}
                                </button>
                              ) : (
                                <span className="text-[11px] text-gray-400">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 sm:px-6 pb-5 pt-2 flex gap-2.5 flex-shrink-0 border-t border-gray-100 dark:border-gray-700/80">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm font-semibold py-3 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold py-3 transition-colors disabled:opacity-70 shadow-sm"
          >
            {submitting
              ? (mode === 'today' ? 'Applying…' : 'Submitting…')
              : (mode === 'today' ? 'Apply Leave' : 'Request Leave')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ApplyLeaveModal;
